import { History, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { NumberInput } from '@/components/ui/number-input'
import { Select } from '@/components/ui/select'
import { fmtMoney } from '../../shared/format'
import { type PastPaymentInput, usePastPayment, usePayPastItems } from '../api'
import { OBLIGATION_KINDS, PAYMENT_CHANNELS, type Obligation, type PaymentChannel } from '../types'
import { toastError, useFinanceLookups } from '../ui'
import { Field, FormSection } from './FormBits'

// Yerel tarih (UTC'ye çevirmek gece yarısına yakın saatlerde günü kaydırır).
const today = () => new Date().toLocaleDateString('sv-SE')
const yesterday = () => {
  const d = new Date()
  d.setDate(d.getDate() - 1)
  return d.toLocaleDateString('sv-SE')
}

function useAccountOptions() {
  const { accounts, accountDetail } = useFinanceLookups()
  return accounts
    .filter((a) => a.active)
    .map((a) => ({ value: a.id, label: a.name, description: [accountDetail(a.id), fmtMoney(a.balance)].filter(Boolean).join(' · ') }))
}

/**
 * Sistem öncesinden tek seferlik ödeme ya da tahsilat (geçmiş bir vergi, SGK primi, kira…). Kayıt ödeme tarihiyle
 * işlenir; kasa/banka hareketi o tarihe düşer. Form kaydettikten sonra açık kalır, sıradaki ödeme girilebilir.
 */
export function PastPaymentModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation()
  const save = usePastPayment()
  const { categories } = useFinanceLookups()
  const accountOptions = useAccountOptions()
  const empty: PastPaymentInput = {
    title: '',
    kind: 'tax',
    direction: 'out',
    categoryId: '',
    partyId: '',
    period: '',
    dueDate: '',
    paidOn: '',
    amount: '',
    accountId: '',
    channel: 'transfer',
    docNo: '',
    note: '',
  }
  const [form, setForm] = useState<PastPaymentInput>(empty)
  const [submitted, setSubmitted] = useState(false)
  const set = <K extends keyof PastPaymentInput>(k: K, v: PastPaymentInput[K]) => setForm((f) => ({ ...f, [k]: v }))
  const errors = {
    title: submitted && !form.title.trim() ? t('finance.obligations.past.titleRequired') : undefined,
    paidOn: submitted && (!form.paidOn || form.paidOn > today()) ? t('finance.obligations.past.paidOnPast') : undefined,
    amount: submitted && !(Number(form.amount) > 0) ? t('finance.form.amountInvalid') : undefined,
    account: submitted && !form.accountId ? t('finance.pay.form.accountRequired') : undefined,
  }
  const kindCategory = form.direction === 'in' ? 'income' : 'expense'

  const submit = () => {
    setSubmitted(true)
    if (!form.title.trim() || !form.paidOn || form.paidOn > today() || !(Number(form.amount) > 0) || !form.accountId) return
    save.mutate(form, {
      onSuccess: () => {
        toast.success(t('finance.obligations.past.saved', { title: form.title.trim() }))
        setForm((f) => ({ ...empty, kind: f.kind, direction: f.direction, categoryId: f.categoryId, accountId: f.accountId, channel: f.channel, title: f.title }))
        setSubmitted(false)
      },
      onError: toastError,
    })
  }

  return (
    <Modal
      open={open}
      size="lg"
      onOpenChange={(v) => !v && onClose()}
      dismissible={!save.isPending}
      icon={<History />}
      title={t('finance.obligations.past.title')}
      description={t('finance.obligations.past.hint')}
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onClose}>
            {t('common.close')}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <FormSection>
        <Field id="pp-title" label={t('finance.obligations.form.title')} error={errors.title} full>
          <Input id="pp-title" maxLength={160} value={form.title} placeholder={t('finance.obligations.past.titlePh')} onChange={(e) => set('title', e.target.value)} aria-invalid={!!errors.title} />
        </Field>
        <Field id="pp-kind" label={t('finance.obligations.form.kind')}>
          <Select id="pp-kind" value={form.kind} onChange={(v) => set('kind', v)} options={OBLIGATION_KINDS.map((k) => ({ value: k, label: t(`finance.obligations.kinds.${k}`) }))} />
        </Field>
        <Field id="pp-dir" label={t('finance.obligations.form.direction')}>
          <Select
            id="pp-dir"
            value={form.direction}
            onChange={(v) => setForm((f) => ({ ...f, direction: v, categoryId: '' }))}
            options={(['out', 'in'] as const).map((d) => ({ value: d, label: t(`finance.obligations.direction.${d}`) }))}
          />
        </Field>
        <Field id="pp-cat" label={t('finance.obligations.past.category')} hint={t('finance.obligations.past.categoryHint')}>
          <Select
            id="pp-cat"
            searchable
            value={form.categoryId}
            onChange={(v) => set('categoryId', v)}
            placeholder={t('finance.obligations.past.autoCategory')}
            options={[{ value: '', label: t('finance.obligations.past.autoCategory') }, ...categories.filter((c) => c.active && c.kind === kindCategory).map((c) => ({ value: c.id, label: c.name }))]}
          />
        </Field>
        <Field id="pp-period" label={t('finance.obligations.past.period')} hint={t('finance.obligations.past.periodHint')}>
          <Input id="pp-period" type="month" value={form.period} onChange={(e) => set('period', e.target.value)} />
        </Field>
        <Field id="pp-paid" label={t('finance.pay.date')} error={errors.paidOn}>
          <DateField id="pp-paid" value={form.paidOn} max={today()} onChange={(v) => set('paidOn', v)} aria-invalid={!!errors.paidOn} />
        </Field>
        <Field id="pp-amount" label={t(form.direction === 'in' ? 'finance.pay.form.amountIn' : 'finance.pay.form.amountOut')} error={errors.amount}>
          <NumberInput id="pp-amount" value={form.amount} onChange={(v) => set('amount', v)} minDecimalsOnBlur={2} suffix="₺" aria-invalid={!!errors.amount} />
        </Field>
        <Field id="pp-account" label={t('finance.pay.account')} error={errors.account}>
          <Select id="pp-account" value={form.accountId} onChange={(v) => set('accountId', v)} placeholder={t('common.select')} options={accountOptions} invalid={!!errors.account} />
        </Field>
        <Field id="pp-channel" label={t('finance.pay.channel')}>
          <Select id="pp-channel" value={form.channel} onChange={(v) => set('channel', v)} options={PAYMENT_CHANNELS.map((c) => ({ value: c, label: t(`finance.channel.${c}`) }))} />
        </Field>
        <Field id="pp-doc" label={t('finance.obligations.pay.docNo')}>
          <Input id="pp-doc" maxLength={60} value={form.docNo} onChange={(e) => set('docNo', e.target.value)} />
        </Field>
        <Field id="pp-note" label={t('finance.pay.note')}>
          <Input id="pp-note" maxLength={240} value={form.note} onChange={(e) => set('note', e.target.value)} />
        </Field>
      </FormSection>
    </Modal>
  )
}

/** Geçmişten başlayan tanımın vadesi geçmiş bekleyen kalemlerini toplu "ödendi" işaretler (her biri vade tarihinde). */
export function PayPastModal({ obligation, onClose }: { obligation: Obligation | null; onClose: () => void }) {
  const { t } = useTranslation()
  const pay = usePayPastItems()
  const accountOptions = useAccountOptions()
  const [accountId, setAccountId] = useState(obligation?.accountId ?? '')
  const [until, setUntil] = useState(yesterday())
  const [channel, setChannel] = useState<PaymentChannel>('transfer')
  const [submitted, setSubmitted] = useState(false)
  if (!obligation) return null

  const submit = () => {
    setSubmitted(true)
    if (!accountId || !until || until >= today()) return
    pay.mutate(
      { id: obligation.id, accountId, until, channel },
      {
        onSuccess: (r) => {
          toast.success(t('finance.obligations.payPast.done', { count: r?.paid ?? 0 }))
          onClose()
        },
        onError: toastError,
      },
    )
  }

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onClose()}
      dismissible={!pay.isPending}
      icon={<History />}
      title={t('finance.obligations.payPast.title', { title: obligation.title })}
      description={t('finance.obligations.payPast.hint')}
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={pay.isPending}>
            {pay.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('finance.obligations.payPast.submit')}
          </Button>
        </>
      }
    >
      <FormSection>
        <Field id="pt-until" label={t('finance.obligations.payPast.until')} error={submitted && (!until || until >= today()) ? t('finance.obligations.past.paidOnPast') : undefined}>
          <DateField id="pt-until" value={until} max={yesterday()} onChange={setUntil} clearable={false} />
        </Field>
        <Field id="pt-account" label={t('finance.pay.account')} error={submitted && !accountId ? t('finance.pay.form.accountRequired') : undefined}>
          <Select id="pt-account" value={accountId} onChange={setAccountId} placeholder={t('common.select')} options={accountOptions} />
        </Field>
        <Field id="pt-channel" label={t('finance.pay.channel')}>
          <Select id="pt-channel" value={channel} onChange={(v) => setChannel(v)} options={PAYMENT_CHANNELS.map((c) => ({ value: c, label: t(`finance.channel.${c}`) }))} />
        </Field>
      </FormSection>
    </Modal>
  )
}
